import { useState, useCallback } from 'react';
import { Moon, Sun, Settings, LogOut, Info, type LucideIcon } from 'lucide-react';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { apiClient } from '@/api/client';
import { MobileBottomSheet } from '@/components/mobile/MobileBottomSheet';

// ═══════════════════════════════════════════
// More Screen
// ═══════════════════════════════════════════

export function MoreScreen() {
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const [showSettings, setShowSettings] = useState(false);

  const currentUser = apiClient.getCurrentUser();

  const handleLogout = useCallback(() => {
    apiClient.logout();
  }, []);

  return (
    <div className="h-full overflow-y-auto">
      {/* Options list */}
      <div className="px-4 pt-3 space-y-2">
        {/* Theme toggle */}
        <OptionRow
          icon={theme === 'dark' ? Moon : Sun}
          label={theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
          right={
            <ToggleSwitch
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
          }
        />

        {/* Settings */}
        <OptionRow
          icon={Settings}
          label="Settings"
          chevron
          onTap={() => setShowSettings(true)}
        />

        {/* Account */}
        {currentUser && (
          <div
            className="rounded-lg border px-3 py-3"
            style={{
              backgroundColor: 'var(--dbgo-panel)',
              borderColor: 'var(--border-default)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                style={{ backgroundColor: 'var(--accent-blue)' }}
              >
                {(currentUser.displayName || currentUser.username).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-text-primary font-medium truncate">
                  {currentUser.displayName || currentUser.username}
                </div>
                <div className="text-xs text-text-muted">
                  {currentUser.role}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-3 w-full flex items-center justify-center gap-2 rounded-md border text-sm text-accent-red py-2"
              style={{ borderColor: 'var(--border-default)' }}
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        )}
      </div>

      {/* About section */}
      <div className="px-4 pt-6 pb-4">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
          About
        </div>
        <div
          className="rounded-lg border px-3 py-3"
          style={{
            backgroundColor: 'var(--dbgo-panel)',
            borderColor: 'var(--border-default)',
          }}
        >
          <div className="flex items-center gap-2">
            <Info size={16} className="text-accent-blue" />
            <div>
              <div className="text-sm text-text-primary font-semibold">DBGo</div>
              <div className="text-xs text-text-muted">Database Management Tool</div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Bottom Sheet */}
      {showSettings && (
        <SettingsSheet onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Shared components
// ═══════════════════════════════════════════

interface OptionRowProps {
  icon: LucideIcon;
  label: string;
  right?: React.ReactNode;
  chevron?: boolean;
  onTap?: () => void;
}

function OptionRow({ icon: Icon, label, right, chevron, onTap }: OptionRowProps) {
  return (
    <button
      onClick={onTap}
      disabled={!onTap && !chevron}
      className="w-full flex items-center gap-3 rounded-lg border px-3 active:bg-dbgo-tab-hover transition-colors"
      style={{
        height: 52,
        backgroundColor: 'var(--dbgo-panel)',
        borderColor: 'var(--border-default)',
      }}
    >
      <Icon size={20} className="text-text-secondary flex-shrink-0" />
      <span className="text-sm text-text-primary flex-1 text-left">{label}</span>
      {right}
      {chevron && (
        <span className="text-text-muted text-sm">&rsaquo;</span>
      )}
    </button>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className="relative inline-flex items-center rounded-full transition-colors"
      style={{
        width: 44,
        height: 24,
        backgroundColor: checked ? 'var(--accent-blue)' : 'var(--border-strong)',
      }}
    >
      <span
        className="block rounded-full bg-white shadow transition-transform"
        style={{
          width: 20,
          height: 20,
          transform: checked ? 'translateX(22px)' : 'translateX(2px)',
        }}
      />
    </button>
  );
}

// ═══════════════════════════════════════════
// Settings Bottom Sheet
// ═══════════════════════════════════════════

function SettingsSheet({ onClose }: { onClose: () => void }) {
  const save = useSettingsStore((s) => s.save);

  const editorFontSize = useSettingsStore((s) => s.getNumber('editor.fontSize', 14));
  const terminalFontSize = useSettingsStore((s) => s.getNumber('terminal.fontSize', 13));
  const queryTimeout = useSettingsStore((s) => s.getNumber('query.timeout', 30));

  const handleSave = useCallback(
    (key: string, value: string) => {
      save({ [key]: value }).catch(() => {
        // silently fail
      });
    },
    [save],
  );

  return (
    <MobileBottomSheet title="Settings" onClose={onClose} snapPoints={[0.6, 0.85]}>
      <div className="px-4 py-2 space-y-4">
        <SettingsGroup title="Editor">
          <NumberSetting
            label="Font Size"
            value={editorFontSize}
            onChange={(v) => handleSave('editor.fontSize', String(v))}
            min={10}
            max={24}
          />
        </SettingsGroup>

        <SettingsGroup title="Terminal">
          <NumberSetting
            label="Font Size"
            value={terminalFontSize}
            onChange={(v) => handleSave('terminal.fontSize', String(v))}
            min={10}
            max={24}
          />
        </SettingsGroup>

        <SettingsGroup title="Query">
          <NumberSetting
            label="Timeout (seconds)"
            value={queryTimeout}
            onChange={(v) => handleSave('query.timeout', String(v))}
            min={5}
            max={300}
          />
        </SettingsGroup>
      </div>
    </MobileBottomSheet>
  );
}

function SettingsGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function NumberSetting({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) {
  return (
    <div
      className="flex items-center justify-between rounded-lg border px-3"
      style={{
        height: 44,
        backgroundColor: 'var(--dbgo-input)',
        borderColor: 'var(--border-default)',
      }}
    >
      <span className="text-sm text-text-primary">{label}</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          className="w-7 h-7 flex items-center justify-center rounded-md text-text-primary active:bg-dbgo-tab-hover"
          style={{ backgroundColor: 'var(--dbgo-panel)' }}
        >
          -
        </button>
        <span className="text-sm text-text-primary w-8 text-center">{value}</span>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          className="w-7 h-7 flex items-center justify-center rounded-md text-text-primary active:bg-dbgo-tab-hover"
          style={{ backgroundColor: 'var(--dbgo-panel)' }}
        >
          +
        </button>
      </div>
    </div>
  );
}
